/**
 * Approval Detail Modal
 * Shows full details of a pending change with before/after comparison
 * For contract approvals, shows rich preview with contract summary,
 * impact metrics, and amortization schedule (like Rate Change Impact Assessment)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Verified as VerifiedIcon,
  HelpOutline as QueryIcon,
  Undo as ReverseIcon,
  History as HistoryIcon,
  CompareArrows as CompareIcon,
  AttachFile as AttachmentIcon,
  AccountBalance as AccountBalanceIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Reply as ReplyIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost, apiUpload } from '../../utils/apiClient';
import { formatCurrency, formatNumber, formatRatePct } from '../../utils/formatters';
import { exportAmortizationSchedule } from '../../utils/excelExport';
import logger from '../../utils/logger';
import TabPanel from '../common/TabPanel';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import useDocumentSummaries from '../../hooks/useDocumentSummaries';
import useDocumentActions from '../../hooks/useDocumentActions';
import usePagination from '../../hooks/usePagination';
import DocumentPreviewDialog from '../documents/DocumentPreviewDialog';
import ScheduleComparisonTable from '../contracts/ScheduleComparisonTable';
import ImpactSummaryCards from '../contracts/ImpactSummaryCards';
import { buildComparisonRows } from '../../utils/scheduleComparison';
import {
  isTerminalBalanceZero,
  getTerminalBalance,
  SCHEDULE_TERMINAL_BALANCE_TOLERANCE,
} from '../../utils/scheduleValidation';

// Status chip styling
const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'pending_review': return 'info';
    case 'approved': return 'success';
    case 'verified': return 'success';
    case 'rejected': return 'error';
    case 'queried': return 'secondary';
    case 'reversed': return 'error';
    case 'cancelled': return 'default';
    case 'executed': return 'success';
    default: return 'default';
  }
};

const headerCellSx = { backgroundColor: '#f5f5f5', fontWeight: 'bold' };

function ApprovalDetailModal({ open, onClose, item, onAction, permissions }) {
  const { currentUserEmail, currentUserId } = usePermissions();
  const { tenant, isWorkflowDocumentRequired } = useTenant();
  const verificationDocRequired = isWorkflowDocumentRequired('approval_verification');
  const dp = tenant?.roundingPrecision ?? 3;
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [canApproveData, setCanApproveData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  // Pagination for the approval's amortization schedule preview (standard rules).
  const schedulePagination = usePagination({ defaultRowsPerPage: 25 });
  const [adhocCapitalEvents, setAdhocCapitalEvents] = useState([]);
  const [adhocInterestPayments, setAdhocInterestPayments] = useState([]);
  const [finalisationSummary, setFinalisationSummary] = useState(null);
  // Document preview & download (shared hook)
  const docActions = useDocumentActions();

  // The terminal balance is read from the engine-written, persisted outstanding_balance on
  // the last schedule row — NEVER recomputed client-side. This is the single source of truth
  // shared with the backend approve guard (assertTerminalBalanceZero) and the contract detail
  // page; a prior client-side recompute double-counted WHT on accrual rows and produced a
  // phantom non-zero residual that wrongly blocked approval.
  const terminalBalanceOk = isTerminalBalanceZero(scheduleData?.schedule);
  const isContractApprovalWithSchedule =
    item?.change_type === 'contract_approval' &&
    Array.isArray(scheduleData?.schedule) &&
    scheduleData.schedule.length > 0;
  const approveBlockedForBalance = isContractApprovalWithSchedule && !terminalBalanceOk;
  const terminalBalanceForDisplay = getTerminalBalance(scheduleData?.schedule);

  // Resolve proof document IDs to filenames (detail view)
  const detailDocIds = detailData?.proof_document_ids;
  const { documents: proofDocs, reload: reloadProofDocs } = useDocumentSummaries(detailDocIds);

  // Resolve proof document IDs to filenames (list-level chips)
  const itemDocIds = item?.proof_document_ids;
  const { documents: itemProofDocs } = useDocumentSummaries(itemDocIds);

  useEffect(() => {
    if (open && item) {
      loadDetails();
      checkCanApprove();
    } else {
      // Reset state when modal closes
      setCanApproveData(null);
      setContractData(null);
      setScheduleData(null);
      setAdhocCapitalEvents([]);
      setAdhocInterestPayments([]);
      setFinalisationSummary(null);
      docActions.closePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDetails and checkCanApprove are stable functions that run when modal opens
  }, [open, item]);

  const loadDetails = async () => {
    if (!item) return;
    setLoading(true);
    try {
      const response = await apiGet(`/approvals/${item.id}`);
      let detail = null;
      if (response.success) {
        detail = response.data;
        setDetailData(response.data);
        if (response.data.history) {
          setHistory(response.data.history);
        }
      }

      // The contract approval is validated against the SUBMITTED schedule snapshot,
      // captured at submit time in after_snapshot.schedule (T118). This makes approval
      // immutable against any later schedule regeneration and avoids any client recompute.
      // Contracts submitted BEFORE T118 have no snapshot array → fall back to the live schedule.
      const submittedSchedule = Array.isArray(detail?.after_snapshot?.schedule)
        ? detail.after_snapshot.schedule
        : null;

      // For contract approvals, fetch full contract details, schedule, and adhoc events
      if (item.change_type === 'contract_approval' && item.entity_id) {
        const [contractRes, scheduleRes, adhocCapitalRes, adhocInterestRes] = await Promise.allSettled([
          apiGet(`/contracts/${item.entity_id}`),
          submittedSchedule ? Promise.resolve(null) : apiGet(`/amortization/contract/${item.entity_id}`),
          apiGet(`/contracts/${item.entity_id}/adhoc-capital`),
          apiGet(`/contracts/${item.entity_id}/adhoc-interest-payments`),
        ]);
        if (contractRes.status === 'fulfilled' && contractRes.value?.success) {
          setContractData(contractRes.value.data);
        }
        if (submittedSchedule) {
          // Use the immutable submitted snapshot — the source of truth for approval.
          setScheduleData({ schedule: submittedSchedule });
        } else if (scheduleRes.status === 'fulfilled' && scheduleRes.value?.success) {
          setScheduleData(scheduleRes.value.data);
        }
        if (adhocCapitalRes.status === 'fulfilled' && adhocCapitalRes.value?.success) {
          setAdhocCapitalEvents(adhocCapitalRes.value.data || []);
        }
        if (adhocInterestRes.status === 'fulfilled' && adhocInterestRes.value?.success) {
          setAdhocInterestPayments(adhocInterestRes.value.data || []);
        }
      }

      // For finalisations, fetch the per-investor financial summary so the
      // After panel can show projected reserve/payout instead of "No projected state".
      if (item.change_type === 'contract_finalisation' && item.entity_id) {
        const finalisationDate = item.change_data?.finalisation_date;
        if (finalisationDate) {
          try {
            const summaryRes = await apiGet(
              `/contracts/${item.entity_id}/finalisation-summary?finalisation_date=${finalisationDate}`
            );
            if (summaryRes?.success) {
              setFinalisationSummary(summaryRes.data || []);
            }
          } catch (err) {
            // Non-fatal: fall back to the basic Before/After view.
            logger.error('Error loading finalisation summary', { message: err.message });
          }
        }
      }
    } catch (error) {
      logger.error('Error loading approval details', { message: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Check if current user can approve this item (creator/approver separation)
  const checkCanApprove = async () => {
    if (!item || item.change_type !== 'contract_approval') {
      setCanApproveData(null);
      return;
    }
    try {
      const response = await apiGet(`/approvals/${item.id}/can-approve`);
      if (response.success) {
        setCanApproveData(response.data);
      }
    } catch (error) {
      logger.error('Error checking approval eligibility', { message: error.message });
      // If API fails, check locally using change_data
      const contractCreatedBy = item.change_data?.created_by;
      if (contractCreatedBy && currentUserEmail &&
          contractCreatedBy.toLowerCase().trim() === currentUserEmail.toLowerCase().trim()) {
        setCanApproveData({
          can_approve: false,
          reason: 'You cannot approve a contract you created',
          created_by: contractCreatedBy,
        });
      }
    }
  };

  // Use fetched detail data for snapshots if available, fall back to item prop
  const currentItem = detailData || item;

  const formatDateTime = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd MMM yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const formatDateShort = (date) => {
    if (!date) return '-';
    try {
      const str = String(date).substring(0, 10);
      return format(new Date(str + 'T12:00:00'), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  };

  const formatCurrencyDp = (value, currencyCode) => formatCurrency(value, currencyCode || 'ZAR', dp);

  // Determine which actions are available
  const getAvailableActions = () => {
    if (!item) return [];

    const category = item.category;
    const tabKey = `tab_${category === 'contract' ? 'contract_approvals' : 'verifications'}`;
    const isAdmin = permissions[tabKey] === 'admin';

    // Queried verifications: only the submitter can respond (no admin required)
    if (category === 'verification' && item.status === 'queried') {
      const requestedBy = item.requested_by?.toString().toLowerCase().trim();
      const isSubmitter = requestedBy && (
        requestedBy === currentUserEmail?.toLowerCase().trim() ||
        requestedBy === currentUserId?.toString()
      );
      return isSubmitter ? ['respond'] : [];
    }

    if (!isAdmin) return [];

    if (category === 'verification' && item.status === 'pending_review') {
      return ['verify', 'query', 'reverse'];
    }

    if ((category === 'contract' || category === 'system') && item.status === 'pending') {
      if (item.change_type === 'contract_approval' && canApproveData && !canApproveData.can_approve) {
        return ['reject'];
      }
      return ['approve', 'reject'];
    }

    return [];
  };

  const availableActions = getAvailableActions();

  // Summary row for contract details tables
  const SummaryRow = ({ label, value, highlight = false }) => (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ fontWeight: 'medium', width: '40%', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ fontWeight: highlight ? 'bold' : 'normal', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}>
        {value || '-'}
      </TableCell>
    </TableRow>
  );

  // Flow type helpers (matching AmortizationScheduleTable)
  const getFlowType = (narration) => {
    if (!narration) return 'neutral';
    if (narration.includes('Drawdown') || narration.includes('Opening Balance') || narration.includes('Injection')) return 'inflow';
    if (narration.includes('Payment') || narration.includes('Fee') || narration.includes('Duty') || narration.includes('Withdrawal')) return 'outflow';
    if (narration.includes('Accrual')) return 'accrual';
    return 'neutral';
  };

  const getFlowLabel = (narration) => {
    const flowType = getFlowType(narration);
    if (flowType === 'inflow') return { label: 'IN', color: 'info' };
    if (flowType === 'outflow') return { label: 'OUT', color: 'error' };
    if (flowType === 'accrual') return { label: 'ACC', color: 'warning' };
    return { label: '-', color: 'default' };
  };

  const getNarrationColor = (narration) => {
    if (!narration) return 'default';
    if (narration.includes('Injection')) return 'success';
    if (narration.includes('Withdrawal')) return 'warning';
    const flowType = getFlowType(narration);
    if (flowType === 'inflow') return 'info';
    if (flowType === 'outflow') return 'error';
    if (flowType === 'accrual') return 'warning';
    return 'default';
  };

  const formatFlowAmount = (value, flowType) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    if (num === 0) return '-';
    const formatted = formatNumber(num, 2);
    if (flowType === 'outflow') return <span style={{ color: '#d32f2f' }}>({formatted})</span>;
    if (flowType === 'inflow') return <span style={{ color: '#1976d2' }}>{formatted}</span>;
    return formatted;
  };

  // Render contract approval preview (rich layout like Rate Change Impact Assessment)
  const renderContractApprovalPreview = () => {
    const contract = contractData;
    const schedule = scheduleData?.schedule || [];
    const summary = scheduleData?.summary;
    const currencyCode = contract?.currency_code || '';
    const hasWhtData = schedule.some(entry => entry.gross_interest_amount || entry.wht_amount || entry.wht_rate_applied);

    // The Outstanding Balance column renders each row's persisted outstanding_balance
    // (engine-written) — not a client recompute — so it matches the contract detail page
    // and the terminal-balance gate.

    return (
      <Box>
        {schedule.length === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            No amortization schedule exists for this contract. Approval will be blocked until a schedule is generated.
          </Alert>
        )}
        {schedule.length > 0 && !terminalBalanceOk && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Schedule final running balance is {formatNumber(terminalBalanceForDisplay ?? 0, 2)} (tolerance {SCHEDULE_TERMINAL_BALANCE_TOLERANCE.toFixed(2)}). Approval is disabled until the schedule pays down to zero — reject this submission so the schedule clears, then regenerate before resubmitting.
          </Alert>
        )}
        {/* Contract Details - 2 column layout */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Contract Details
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <SummaryRow label="Investor" value={contract.investor_name} />
                  <SummaryRow
                    label="Product"
                    value={contract.product_type && <Chip label={contract.product_type} size="small" />}
                  />
                  <SummaryRow
                    label="Capital Amount"
                    value={formatCurrencyDp(contract.capital_amount, currencyCode)}
                    highlight
                  />
                  <SummaryRow label="Currency" value={currencyCode} />
                  {/* T117/T119: multi-currency visibility — only when the investor settles in a
                      different currency than the contract accrues in. */}
                  {contract.settlement_currency_code && contract.settlement_currency_code !== currencyCode && (
                    <>
                      <SummaryRow
                        label="Settlement Currency"
                        value={`${contract.settlement_currency_code} — capital & interest settle in ${contract.settlement_currency_code} (accrues in ${currencyCode})`}
                        highlight
                      />
                      {contract.settlement_fx_rate != null && (
                        <SummaryRow
                          label="Initial Capital Rate"
                          value={`${contract.settlement_currency_code}/${currencyCode} ${contract.settlement_fx_rate} — agreed for initial capital; interest & later settlements use the live rate`}
                        />
                      )}
                    </>
                  )}
                  <SummaryRow label="Rate Type" value={contract.rate_type} />
                  <SummaryRow
                    label="Interest Rate"
                    value={contract.interest_rate != null ? `${contract.interest_rate}%` : '-'}
                    highlight
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Dates & Payment Structure
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <SummaryRow label="Start Date" value={formatDateShort(contract.deposit_date)} />
                  <SummaryRow label="Maturity Date" value={formatDateShort(contract.maturity_date)} highlight />
                  <SummaryRow
                    label="Funds Received"
                    value={contract.funds_received_date ? formatDateShort(contract.funds_received_date) : 'Same as start date'}
                  />
                  <SummaryRow
                    label="Day Count Basis"
                    value={contract.day_count_basis ? `${contract.day_count_basis} days` : '365 days'}
                  />
                  <SummaryRow label="Interest Frequency" value={contract.interest_paid_frequency} />
                  <SummaryRow label="Capital Frequency" value={contract.capital_paid_frequency || 'At Maturity'} />
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Broker, Stamp Duty & Collateral if applicable */}
          {(contract.is_brokered || contract.has_collateral || (Array.isArray(contract.stamp_duties) && contract.stamp_duties.length > 0) || contract.stamp_duty) && (
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Broker, Stamp Duty & Collateral
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {contract.is_brokered && (
                      <>
                        <SummaryRow label="Broker" value={contract.broker_name} />
                        <SummaryRow
                          label="Broker Fees"
                          value={contract.broker_fees != null
                            ? `${formatRatePct(contract.broker_fees)}% (${currencyCode} ${formatNumber((parseFloat(contract.capital_amount) || 0) * parseFloat(contract.broker_fees) / 100)})`
                            : '-'}
                        />
                      </>
                    )}
                    {/* T113: stamp duty is standalone + multi-row — one line per row,
                        shown whenever present (brokered or not). Falls back to the
                        legacy single column when the array is absent. */}
                    {(Array.isArray(contract.stamp_duties) && contract.stamp_duties.length > 0
                      ? contract.stamp_duties
                      : (contract.stamp_duty && contract.stamp_duty_fees
                          ? [{ reference: 'Stamp Duty', percentage: contract.stamp_duty_fees }]
                          : [])
                    ).map((sd, i) => (
                      <SummaryRow
                        key={sd.id ?? i}
                        label={sd.reference || 'Stamp Duty'}
                        value={`${formatRatePct(sd.percentage)}% (${currencyCode} ${formatNumber((parseFloat(contract.capital_amount) || 0) * parseFloat(sd.percentage) / 100)})`}
                      />
                    ))}
                    {contract.has_collateral && (
                      <>
                        <SummaryRow label="Collateral Provider" value={contract.collateral_provider_name} />
                        <SummaryRow label="Collateral Type" value={contract.collateral_type} />
                        <SummaryRow
                          label="Collateral Amount"
                          value={formatCurrencyDp(contract.collateral_amount, currencyCode)}
                        />
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
        {/* Impact Summary Metrics */}
        {summary && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Schedule Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Capital Amount
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrencyDp(contract.capital_amount, currencyCode)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Interest Rate
                    </Typography>
                    <Typography variant="h6">
                      {contract.interest_rate != null ? `${contract.interest_rate}%` : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Interest
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrencyDp(summary.total_interest, currencyCode)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Schedule Entries
                    </Typography>
                    <Typography variant="h6">
                      {schedule.length}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Adhoc Events Summary */}
              {(adhocCapitalEvents.length > 0 || adhocInterestPayments.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Adhoc Events
                  </Typography>
                  <Grid container spacing={2}>
                    {adhocCapitalEvents.length > 0 && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                            Capital Events ({adhocCapitalEvents.length})
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {adhocCapitalEvents.map((evt, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>{formatDateShort(evt.event_date)}</TableCell>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>
                                    <Chip
                                      label={evt.event_type === 'withdrawal' ? 'Withdrawal' : 'Injection'}
                                      size="small"
                                      color={evt.event_type === 'withdrawal' ? 'warning' : 'success'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.5, border: 0, fontWeight: 'bold' }}>
                                    {formatCurrencyDp(evt.amount, currencyCode)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    )}
                    {adhocInterestPayments.length > 0 && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                            Interest Payment Dates ({adhocInterestPayments.length})
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {adhocInterestPayments.map((evt, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>{formatDateShort(evt.event_date)}</TableCell>
                                  <TableCell sx={{ py: 0.5, border: 0, color: 'text.secondary' }}>
                                    {evt.description || 'Adhoc interest payment'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        {/* Amortization Schedule Table - matches AmortizationScheduleTable columns (minus Status) */}
        {schedule.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Amortization Schedule ({schedule.length} entries)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportAmortizationSchedule(schedule, contract)}
            >
              Export to Excel
            </Button>
          </Box>
        )}
        {schedule.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Seq</TableCell>
                  <TableCell sx={headerCellSx}>Payment Date</TableCell>
                  <TableCell sx={headerCellSx} align="center">Flow</TableCell>
                  <TableCell sx={headerCellSx}>Narration</TableCell>
                  <TableCell sx={headerCellSx} align="right">Rate %</TableCell>
                  <TableCell sx={headerCellSx} align="right">Days</TableCell>
                  <TableCell sx={headerCellSx} align="right">Capital Amount</TableCell>
                  <TableCell sx={headerCellSx} align="right">Interest Amount</TableCell>
                  {hasWhtData && <TableCell sx={headerCellSx} align="right">Withholding Tax</TableCell>}
                  <TableCell sx={headerCellSx} align="right">Payment Amount</TableCell>
                  <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  <TableCell sx={headerCellSx} align="right">Outstanding Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedulePagination.paginateData(schedule).map((entry, idx) => {
                  const narration = entry.narration || '';
                  const flowType = getFlowType(narration);
                  const flowLabel = getFlowLabel(narration);
                  // Seq reflects the row's absolute position so numbering stays
                  // continuous across pages.
                  const seq = schedulePagination.page * schedulePagination.rowsPerPage + idx + 1;

                  return (
                    <TableRow
                      key={seq}
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}
                    >
                      <TableCell>{seq}</TableCell>
                      <TableCell>{formatDateShort(entry.payment_date)}</TableCell>
                      <TableCell align="center">
                        <Chip label={flowLabel.label} size="small" color={flowLabel.color} sx={{ minWidth: 40, fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={narration || 'N/A'} size="small" color={getNarrationColor(narration)} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        {entry.interest_rate != null ? parseFloat(entry.interest_rate).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell align="right">{entry.days_in_period || '-'}</TableCell>
                      <TableCell align="right">
                        {formatFlowAmount(entry.capital_amount, flowType)}
                      </TableCell>
                      <TableCell align="right">
                        {formatFlowAmount(entry.interest_amount, flowType)}
                      </TableCell>
                      {hasWhtData && (
                        <TableCell align="right">
                          {entry.wht_amount && parseFloat(entry.wht_amount) > 0 ? (
                            <span style={{ color: '#9c27b0' }}>({formatNumber(parseFloat(entry.wht_amount), 2)})</span>
                          ) : '-'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <strong>{formatFlowAmount(entry.payment_amount, flowType)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const capital = parseFloat(entry.capital_amount) || 0;
                          const interest = parseFloat(entry.interest_amount) || 0;
                          const wht = parseFloat(entry.wht_amount) || 0;
                          const total = capital + interest + wht;
                          if (total === 0) return '-';
                          return formatFlowAmount(total, flowType);
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(parseFloat(entry.outstanding_balance) || 0, 2)}</strong>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">No amortization schedule available for this contract.</Alert>
        )}
        {schedule.length > 0 && (
          <TablePagination {...schedulePagination.tableProps(schedule.length)} />
        )}
      </Box>
    );
  };

  // Field configuration for verification snapshot rendering
  const SNAPSHOT_FIELD_CONFIG = {
    capital_receipt: [
      { key: 'investor_name',       label: 'Investor',            format: 'text' },
      { key: 'contract_reference',  label: 'Contract Reference',  format: 'text', highlight: true },
      { key: 'amount',              label: 'Amount',              format: 'currency' },
      { key: 'received_date',       label: 'Received Date',       format: 'date' },
      { key: 'reference',           label: 'External Reference',  format: 'text', highlight: true },
      { key: 'payment_method',      label: 'Payment Method',      format: 'text' },
      { key: 'currency_code',       label: 'Currency',            format: 'text' },
      { key: 'notes',               label: 'Notes',               format: 'text' },
    ],
    payment_match: [
      { key: 'contract_reference',  label: 'Contract Reference',  format: 'text', highlight: true },
      { key: 'investor_name',       label: 'Investor',            format: 'text' },
      { key: 'payment_amount',      label: 'Payment Amount',      format: 'currency' },
      { key: 'payment_date',        label: 'Payment Date',        format: 'date' },
      { key: 'payment_reference',   label: 'External Reference',  format: 'text', highlight: true },
      { key: 'payment_type',        label: 'Payment Type',        format: 'text' },
      { key: 'expected_amount',     label: 'Expected Amount',     format: 'currency' },
      { key: 'schedule_date',       label: 'Schedule Date',       format: 'date' },
      { key: 'schedule_narration',  label: 'Schedule Entry Type', format: 'text' },
      { key: 'currency_code',       label: 'Currency',            format: 'text' },
      { key: 'status',              label: 'Status',              format: 'text' },
    ],
  };

  const SUPPRESSED_KEYS = new Set([
    'receipt_id', 'investor_id', 'contract_id', 'payment_id', 'schedule_id',
    'schedule_sequence',
  ]);

  const humanizeKey = (key) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const renderSnapshotFields = (snapshot, changeData, changeType) => {
    if (!snapshot && !changeData) return null;
    const config = SNAPSHOT_FIELD_CONFIG[changeType];
    const merged = { ...(changeData || {}), ...(snapshot || {}) };
    const renderedKeys = new Set();

    const fmtValue = (value, fmt) => {
      if (value === null || value === undefined || value === '') return null;
      switch (fmt) {
        case 'currency': return formatCurrencyDp(value, merged.currency_code);
        case 'date': return formatDateShort(value);
        case 'number': return String(value);
        default: return String(value);
      }
    };

    const fields = [];

    if (config) {
      for (const field of config) {
        renderedKeys.add(field.key);
        const val = fmtValue(merged[field.key], field.format);
        if (val == null) continue;
        fields.push(
          <Typography key={field.key} variant="body2" sx={field.highlight ? { fontWeight: 'medium' } : undefined}>
            <strong>{field.label}:</strong> {val}
          </Typography>
        );
      }
    }

    // Generic fallback for unconfigured keys
    for (const [key, value] of Object.entries(snapshot || {})) {
      if (renderedKeys.has(key) || SUPPRESSED_KEYS.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;
      fields.push(
        <Typography key={key} variant="body2">
          <strong>{humanizeKey(key)}:</strong> {String(value)}
        </Typography>
      );
    }

    return fields.length > 0 ? fields : null;
  };

  // Render generic before/after comparison (non-contract types)
  const renderGenericComparison = () => {
    const before = currentItem.before_snapshot;
    const after = currentItem.after_snapshot;
    const changeData = currentItem.change_data;

    // Show amendment details for capital/interest amendments
    const isCapitalAmendment = ['adhoc_capital_withdrawal', 'adhoc_capital_injection'].includes(currentItem.change_type);
    const isInterestAmendment = currentItem.change_type === 'adhoc_interest_payment';
    const isFinalisation = currentItem.change_type === 'contract_finalisation';

    // Build per-investor projected rows for finalisations by joining the
    // summary (capital, interest, WHT, net) with the reviewer's reserve/payout
    // split from change_data.investor_allocations.
    const finalisationRows = (() => {
      if (!isFinalisation || !Array.isArray(finalisationSummary)) return null;
      const allocations = Array.isArray(changeData?.investor_allocations)
        ? changeData.investor_allocations
        : [];
      const allocByInvestor = new Map(
        allocations.map((a) => [Number(a.investor_id), a])
      );
      return finalisationSummary.map((inv) => {
        const alloc = allocByInvestor.get(Number(inv.investor_id)) || {};
        return {
          investor_id: inv.investor_id,
          investor_name: inv.investor_name,
          original_capital: parseFloat(inv.original_capital) || 0,
          accrued_interest: parseFloat(inv.accrued_interest) || 0,
          wht_amount: parseFloat(inv.wht_amount) || 0,
          net_value: parseFloat(inv.net_value) || 0,
          reserve_amount: parseFloat(alloc.reserve_amount) || 0,
          payout_amount: parseFloat(alloc.payout_amount) || 0,
        };
      });
    })();

    const finalisationTotals = (() => {
      if (!finalisationRows) return null;
      return finalisationRows.reduce(
        (acc, r) => ({
          original_capital: acc.original_capital + r.original_capital,
          accrued_interest: acc.accrued_interest + r.accrued_interest,
          wht_amount: acc.wht_amount + r.wht_amount,
          net_value: acc.net_value + r.net_value,
          reserve_amount: acc.reserve_amount + r.reserve_amount,
          payout_amount: acc.payout_amount + r.payout_amount,
        }),
        { original_capital: 0, accrued_interest: 0, wht_amount: 0, net_value: 0, reserve_amount: 0, payout_amount: 0 }
      );
    })();

    return (
      <Grid container spacing={2}>
        {/* Amendment Details */}
        {(isCapitalAmendment || isInterestAmendment) && changeData && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Amendment Details</Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                {changeData.event_type && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {changeData.event_type === 'withdrawal' ? 'Capital Withdrawal' : changeData.event_type === 'injection' ? 'Capital Injection' : 'Interest Payment'}
                    </Typography>
                  </Grid>
                )}
                {changeData.event_date && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Event Date</Typography>
                    <Typography variant="body2">{changeData.event_date}</Typography>
                  </Grid>
                )}
                {changeData.amount && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatNumber(parseFloat(changeData.amount), 2)}
                    </Typography>
                  </Grid>
                )}
                {changeData.penalty_amount && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Penalty</Typography>
                    <Typography variant="body2" color="error.main" fontWeight="medium">
                      {formatNumber(parseFloat(changeData.penalty_amount), 2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {changeData.motivation && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Motivation</Typography>
                  <Typography variant="body2">{changeData.motivation}</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Finalisation details: projected per-investor allocations */}
        {isFinalisation && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Finalisation Details</Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {changeData?.finalisation_date && (
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Finalisation Date</Typography>
                    <Typography variant="body2">{changeData.finalisation_date}</Typography>
                  </Grid>
                )}
                {changeData?.reason && (
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{changeData.reason}</Typography>
                  </Grid>
                )}
              </Grid>

              {finalisationRows && finalisationRows.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Investor</TableCell>
                        <TableCell sx={headerCellSx} align="right">Capital</TableCell>
                        <TableCell sx={headerCellSx} align="right">Interest</TableCell>
                        <TableCell sx={headerCellSx} align="right">WHT</TableCell>
                        <TableCell sx={headerCellSx} align="right">Net</TableCell>
                        <TableCell sx={headerCellSx} align="right">Reserve</TableCell>
                        <TableCell sx={headerCellSx} align="right">Payout</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {finalisationRows.map((r) => (
                        <TableRow key={r.investor_id}>
                          <TableCell>{r.investor_name}</TableCell>
                          <TableCell align="right">{formatNumber(r.original_capital, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.accrued_interest, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.wht_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.net_value, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.payout_amount, 2)}</TableCell>
                        </TableRow>
                      ))}
                      {finalisationTotals && (
                        <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'grey.50' } }}>
                          <TableCell>Total</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.original_capital, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.accrued_interest, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.wht_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.net_value, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.payout_amount, 2)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : finalisationSummary === null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading projected allocations…</Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  Could not load projected per-investor allocations for this finalisation.
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {/* Before/After comparison
            Skipped entirely for finalisations: the Finalisation Details panel
            above is the comparison, and the "Before" snapshot only duplicates
            contract metadata already shown elsewhere in the modal. */}

        {/* Rich current-vs-projected comparison — same view the SUBMITTER saw.
            Rendered when the stored after_snapshot carries the full schedules
            (capital/interest/extension submits persist this at submit time).
            Falls back to the Before/After snapshot cards below otherwise. */}
        {after && Array.isArray(after.current_schedule) && Array.isArray(after.projected_schedule) ? (
          <Grid size={12}>
            <ImpactSummaryCards
              currentInterest={after.current?.total_interest}
              proposedInterest={after.projected?.total_interest}
              difference={after.interest_diff}
              percentageChange={after.current?.total_interest
                ? (after.interest_diff / Math.abs(after.current.total_interest)) * 100
                : null}
              currentWht={after.current?.total_wht}
              proposedWht={after.projected?.total_wht}
              currencyCode={contractData?.currency_code || 'ZAR'}
              dp={dp}
              proposedLabel="Projected Total Interest"
            />
            <Box sx={{ mt: 2 }}>
              <ScheduleComparisonTable
                rows={buildComparisonRows(after.current_schedule, after.projected_schedule, {
                  calcType: after.interest_calc_type,
                  compoundingFrequency: after.compounding_frequency,
                  dayCountBasis: after.day_count_basis,
                  dp,
                })}
                isIncrease={(after.interest_diff || 0) >= 0}
                dp={dp}
                currentLabel="Current"
                proposedLabel="Projected"
                changeTypeLabel={currentItem.change_type.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}
                currentVersion={after.current_version ?? null}
                nextVersion={after.next_version ?? null}
              />
            </Box>
          </Grid>
        ) : isFinalisation ? null : !before && !after ? (
          <Grid size={12}>
            <Alert severity="info">
              No preview comparison available for this change type.
            </Alert>
          </Grid>
        ) : (
          <>
        {/* Before */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
            <Typography variant="subtitle2" color="error.main" gutterBottom>
              Before
            </Typography>
            <Divider sx={{ my: 1 }} />
            {before ? (
              <Box>
                {renderSnapshotFields(before, changeData, currentItem.change_type) || (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No previous state
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* After */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              After
            </Typography>
            <Divider sx={{ my: 1 }} />
            {after ? (
              <Box>
                {renderSnapshotFields(after, changeData, currentItem.change_type) || (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No projected state
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Changes Summary */}
        {after?.changes_summary && after.changes_summary.length > 0 && (
          <Grid size={12}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Changes Summary:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {after.changes_summary.map((change, idx) => (
                  <li key={idx}>{change}</li>
                ))}
              </ul>
            </Alert>
          </Grid>
        )}
          </>
        )}
      </Grid>
    );
  };

  // Render verification-specific detail view (replaces Before/After for verifications)
  const renderVerificationDetails = () => {
    const after = currentItem.after_snapshot;
    const changeData = currentItem.change_data;
    const isVerification = currentItem.category === 'verification';
    const requestedBy = currentItem.requested_by?.toString().toLowerCase().trim();
    const isSubmitter = requestedBy && (
      requestedBy === currentUserEmail?.toLowerCase().trim() ||
      requestedBy === currentUserId?.toString()
    );
    const canUpload = isVerification && currentItem.status === 'queried' && isSubmitter;

    return (
      <Box>
        {/* Verification Details Card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Verification Details</Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1}>
            {renderSnapshotFields(after, changeData, currentItem.change_type)?.map((field, idx) => (
              <Grid key={idx} size={{ xs: 12, sm: 6 }}>
                {field}
              </Grid>
            )) || (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">No details available</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Proof Documents */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Proof Documents</Typography>
          <Divider sx={{ my: 1 }} />
          {detailData?.proof_document_ids && detailData.proof_document_ids.length > 0 ? (
            <Grid container spacing={1} sx={{ mb: canUpload ? 2 : 0 }}>
              {detailData.proof_document_ids.map((docId, idx) => {
                const docMeta = proofDocs.find(d => d.id === docId);
                return (
                  <Grid size={6} key={docId}>
                    <Chip
                      icon={<PreviewIcon />}
                      label={docMeta?.file_name || `Document ${idx + 1}`}
                      onClick={() => docActions.openPreview(docId)}
                      clickable
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: canUpload ? 2 : 0 }}>
              No documents attached
            </Typography>
          )}

          {/* Upload new document (submitter on queried items) */}
          {canUpload && (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Attach additional document{verificationDocRequired ? ' (Required)' : ' (Optional)'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('document_type', 'proof_of_verification');
                        if (changeData?.contract_id) formData.append('contract_id', changeData.contract_id);
                        if (changeData?.investor_id) formData.append('investor_id', changeData.investor_id);
                        const uploadRes = await apiUpload('/admin/documents/upload', formData);
                        if (uploadRes?.data?.id) {
                          await apiPost(`/approvals/${item.id}/documents`, {
                            document_id: uploadRes.data.id,
                          });
                          // Reload details and document names
                          loadDetails();
                          reloadProofDocs();
                        }
                      } catch (err) {
                        logger.error('Error uploading document', { message: err.message });
                      }
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // Render comparison - delegates to rich preview for contract approvals
  const renderComparison = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (item.change_type === 'contract_approval' && contractData) {
      return renderContractApprovalPreview();
    }

    if (item.category === 'verification') {
      return renderVerificationDetails();
    }

    return renderGenericComparison();
  };

  // Render history timeline
  const renderHistory = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (history.length === 0) {
      return (
        <Alert severity="info">No history available.</Alert>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status Change</TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  <Chip
                    label={entry.action}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{entry.action_by}</TableCell>
                <TableCell>{formatDateTime(entry.action_at)}</TableCell>
                <TableCell>
                  {entry.status_before && (
                    <>
                      <Chip label={entry.status_before} size="small" sx={{ mr: 0.5 }} />
                      {' → '}
                    </>
                  )}
                  <Chip
                    label={entry.status_after}
                    size="small"
                    color={getStatusColor(entry.status_after)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                    {entry.comments || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={['contract_approval', 'adhoc_capital_withdrawal', 'adhoc_capital_injection', 'adhoc_interest_payment', 'contract_extension', 'contract_rollover'].includes(item?.change_type) ? 'xl' : 'lg'}
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {item.entity_reference || `#${item.entity_id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.change_type.replace(/_/g, ' ')} - {item.entity_type}
            </Typography>
          </Box>
          <Chip
            label={item.status.replace(/_/g, ' ')}
            color={getStatusColor(item.status)}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Warning if user cannot approve (creator/approver separation) */}
        {item.change_type === 'contract_approval' && canApproveData && !canApproveData.can_approve && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Cannot Approve
            </Typography>
            <Typography variant="body2">
              {canApproveData.reason}
            </Typography>
            {canApproveData.created_by && (
              <Typography variant="caption" color="text.secondary">
                Contract created by: {canApproveData.created_by}
              </Typography>
            )}
          </Alert>
        )}

        {/* Summary */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {item.change_summary}
        </Alert>

        {/* Query alert for queried items */}
        {item.status === 'queried' && item.reviewed_by && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Queried by {item.reviewed_by}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {item.review_comments || 'No comments provided'}
            </Typography>
            {item.reviewed_at && (
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(item.reviewed_at)}
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Requested By
            </Typography>
            <Typography variant="body2">{item.requested_by}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Requested At
            </Typography>
            <Typography variant="body2">{formatDateTime(item.requested_at)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Typography variant="body2">
              <Chip label={item.priority} size="small" />
            </Typography>
          </Grid>
        </Grid>

        {/* Proof documents (non-verification items only; verifications show docs in their detail view) */}
        {item.category !== 'verification' && item.proof_document_ids && item.proof_document_ids.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AttachmentIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Attached:
              </Typography>
            </Box>
            <Grid container spacing={1}>
              {item.proof_document_ids.map((docId, idx) => {
                const docMeta = itemProofDocs.find(d => d.id === docId);
                return (
                  <Grid size={6} key={docId}>
                    <Chip
                      icon={<PreviewIcon />}
                      label={docMeta?.file_name || `Document ${idx + 1}`}
                      onClick={() => docActions.openPreview(docId)}
                      clickable
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Tabs for details */}
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={item.change_type === 'contract_approval' ? <AccountBalanceIcon fontSize="small" /> : <CompareIcon fontSize="small" />}
            iconPosition="start"
            label={item.change_type === 'contract_approval' ? 'Contract Assessment' : item.category === 'verification' ? 'Verification Details' : 'Before / After'}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label="History"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {renderComparison()}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {renderHistory()}
        </TabPanel>

        {/* Reviewer comments */}
        {item.reviewed_by && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Review Comments
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{item.reviewed_by}</strong> ({formatDateTime(item.reviewed_at)}):
              </Typography>
              <Typography variant="body2">
                {item.review_comments || 'No comments'}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Action input */}
        {availableActions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Comments"
              placeholder="Add comments for your action..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        {availableActions.includes('reject') && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => onAction('reject', comments)}
            disabled={!comments.trim()}
          >
            Reject
          </Button>
        )}
        {availableActions.includes('reverse') && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<ReverseIcon />}
            onClick={() => onAction('reverse', comments)}
            disabled={!comments.trim()}
          >
            Reverse
          </Button>
        )}
        {availableActions.includes('query') && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<QueryIcon />}
            onClick={() => onAction('query', comments)}
            disabled={!comments.trim()}
          >
            Query
          </Button>
        )}

        {/* Spacer: physically separates destructive/intermediate actions from
            positive actions (Approve/Verify/Respond) to reduce misclick risk. */}
        <Box sx={{ flex: 1 }} />

        {availableActions.includes('respond') && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={() => onAction('respond', comments)}
            disabled={!comments.trim()}
          >
            Respond
          </Button>
        )}
        {availableActions.includes('verify') && (
          <Tooltip
            title={
              verificationDocRequired && (!detailDocIds || detailDocIds.length === 0)
                ? 'A verification document must be attached before this item can be verified.'
                : ''
            }
          >
            <span>
              <Button
                color="success"
                variant="contained"
                startIcon={<VerifiedIcon />}
                onClick={() => onAction('verify', comments)}
                disabled={verificationDocRequired && (!detailDocIds || detailDocIds.length === 0)}
              >
                Verify
              </Button>
            </span>
          </Tooltip>
        )}
        {availableActions.includes('approve') && (
          <Tooltip
            title={
              approveBlockedForBalance
                ? `Schedule final balance is ${formatNumber(terminalBalanceForDisplay ?? 0, 2)} — must be within ${SCHEDULE_TERMINAL_BALANCE_TOLERANCE.toFixed(2)} of zero. Reject and regenerate the schedule.`
                : ''
            }
          >
            <span>
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={() => onAction('approve', comments)}
                disabled={approveBlockedForBalance}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog {...docActions} />
    </Dialog>
  );
}

export default ApprovalDetailModal;
